import { Vue, Component, Ref, Prop, Emit } from 'vue-property-decorator'
import { uniqueId } from 'lodash'
import { Nullable } from '@/shared/types/app.types'
import { commonSampleClassificationService } from '@/api/resources/common/sample-classification/sample-classification.service'
import { BModal } from 'bootstrap-vue'
import { initialLocalAutomationPreformattedModalState, LocalAutomationPreformattedModalState } from '@/modules/configurationModule/detail/automation/preformatted/components/preformatted-modal/configuration-automation-preformatted-modal.model'
import { ConfigurationAutomaticPreformattedByOntologyCreateRequest, ConfigurationAutomaticPreformattedByOntologyPatchInBatchRequest, ConfigurationAutomaticPreformattedByOntologyResponse } from '@/modules/configurationModule/detail/automation/preformatted/services/configuration-preformatted.dto'
import { preformattedServices } from '@/api/resources/common/preformatted/preformatted.service'
import { PreformattedIndexResponse } from '@/api/resources/common/preformatted/preformatted.dto'
import { CommonSampleTypeResponse } from '@/api/services/common/sample-type/common-sample-type.dto'
import { CommonTopographicCodeResponse } from '@/api/services/common/topographic-code/common-topographic-code.dto'
import { CommonProcedureResponse } from '@/api/services/common/procedure/common-procedure.dto'
import { CommonMostLikelyMorphologicCodeResponse } from '@/api/services/common/most-likely-morphologic-code/common-most-likely-morphologic-code.dto'

@Component
export default class AutomationPreformattedModal extends Vue {
    @Ref() readonly localAutomationPreformattedModal!: BModal
    @Prop(String) readonly actionTitle!: string
    @Prop(Number) readonly laboratoryId!: number
    @Emit()
    update(defaultPortionTypePatch: ConfigurationAutomaticPreformattedByOntologyPatchInBatchRequest[]): ConfigurationAutomaticPreformattedByOntologyPatchInBatchRequest[] {
        return defaultPortionTypePatch
    }
    @Emit()
    create(defaultPortionTypeCreate: ConfigurationAutomaticPreformattedByOntologyCreateRequest): ConfigurationAutomaticPreformattedByOntologyCreateRequest {
        return defaultPortionTypeCreate
    }

    state: LocalAutomationPreformattedModalState
    constructor() {
        super()
        this.state = initialLocalAutomationPreformattedModalState()
    }

    get canSelectProcedure(): boolean {
        return !!this.state.topographicCodes?.id!
    }

    get canSelectMostLikelyMorphologicCode(): boolean {
        return !!this.state.procedures?.id!
    }

    get canUpdateCombo(): boolean {
        return !this.state?.id
    }

    get canUpdate(): boolean {
        const hasError: boolean = !!this.errors.count()
        const anyFieldIsDirty: boolean = this.$_someFieldIsDirty(this.fields)
        const comboSelectionValid: boolean = !!this.state.sampleTypes?.id && !!this.state.preformatted?.id
        return !hasError && anyFieldIsDirty && comboSelectionValid
    }

    show(selectedRow: Nullable<ConfigurationAutomaticPreformattedByOntologyResponse>): void {
        this.state = initialLocalAutomationPreformattedModalState()
        this.$_resetValidator(this.$validator)
        if (selectedRow) {
            this.updateRow(selectedRow)
        }
        this.localAutomationPreformattedModal.show()
        this.state.uid = uniqueId()
    }

    hide(): void { this.localAutomationPreformattedModal.hide() }

    updateRow(selectedRow: Nullable<ConfigurationAutomaticPreformattedByOntologyResponse>): void {
        this._setSelectedRow(selectedRow)
    }

    spreadEvent(): void {
        if (this.state?.id) {
            const patchRow: ConfigurationAutomaticPreformattedByOntologyPatchInBatchRequest = {
                id: this.state.id,
                preformattedId: this.state.preformatted?.id
            }
            this.update([patchRow])
        } else {
            const postRow: ConfigurationAutomaticPreformattedByOntologyCreateRequest = {
                sampleTypeId: this.state.sampleTypes?.id!,
                preformattedId: this.state.preformatted?.id!,
                topographicCodeId: this.state.topographicCodes?.id,
                procedureId: this.state.procedures?.id,
                mostLikelyMorphologicCodeId: this.state.mostLikelyMorphologicCode?.id
            }
            this.create(postRow)
        }
    }

    getCommonSampleTypes(): Promise<CommonSampleTypeResponse[]> {
        return commonSampleClassificationService.getCommonSampleTypeSampleClassificationsByLaboratory({ laboratoryId: this.laboratoryId })
    }

    getCommonPreformattedTypes(): Promise<PreformattedIndexResponse> {
        return preformattedServices.getAllPreformatted(this.laboratoryId)
    }

    getCommonTopographicCodes(): Promise<CommonTopographicCodeResponse[]> {
        return commonSampleClassificationService.getCommonTopographicCodesSampleClassificationsByLaboratory({ laboratoryId: this.laboratoryId })
    }

    getCommonProcedures(): Promise<CommonProcedureResponse[]> {
        return commonSampleClassificationService.getCommonProceduresSampleClassificationsByLaboratory({ laboratoryId: this.laboratoryId })
    }

    getCommonMostLikelyMorphologicCodeTypes(): Promise<CommonMostLikelyMorphologicCodeResponse[]> {
        return commonSampleClassificationService.getCommonMostLikelyMorphologicCodesSampleClassificationsByLaboratory({ laboratoryId: this.laboratoryId })
    }

    private _setSelectedRow(selectedRow: Nullable<ConfigurationAutomaticPreformattedByOntologyResponse>): void {
        this.state = {
            ...this.state,
            id: selectedRow?.id,
            sampleTypes: selectedRow?.sampleType,
            topographicCodes: selectedRow?.topographicCode,
            procedures: selectedRow?.procedure,
            preformatted: selectedRow?.preformatted,
            mostLikelyMorphologicCode: selectedRow?.mostLikelyMorphologicCode
        }
    }
}
